import { ProviderEvents } from '@openfeature/web-sdk';
import { getEnv } from '@risksmart-app/components/src/utils/environment';
import { featureFlagDefinitions } from '@risksmart-app/feature-flags/src/flags';
import type { FeatureFlag } from '@risksmart-app/modules/src/index';
import { useContext, useEffect, useReducer } from 'react';

import { flagClient } from './client';
import { E2eFlagOverridesContext } from './E2eFlagOverridesContext';

/**
 * Re-renders the consumer whenever flag values can have changed — provider
 * readiness, configuration updates, or context changes — so values read during
 * render stay current.
 */
const useFlagChangeSubscription = (): void => {
  const [, bumpRevision] = useReducer((n: number) => n + 1, 0);
  useEffect(() => {
    const events = [
      ProviderEvents.Ready,
      ProviderEvents.ConfigurationChanged,
      ProviderEvents.ContextChanged,
    ];
    const handler = (): void => {
      bumpRevision();
    };
    events.forEach((event) => flagClient.addHandler(event, handler));

    return () => {
      events.forEach((event) => flagClient.removeHandler(event, handler));
    };
  }, []);
};

/**
 * Hook to check if a feature flag is enabled. E2E organisations resolve from
 * the seeded override supplied by `FeatureFlagProvider`; all other
 * organisations resolve through the provider registered at boot
 * (`initialisePortalFeatureFlags`). The `REACT_APP_FEATURE_<flag>` env var
 * force-ENABLES a flag for local development (it cannot force-disable).
 *
 * This hook does not itself guard against an unreachable provider —
 * `FeatureFlagProvider` blocks children until the provider is healthy and
 * renders an error otherwise, so a value read here is never a fallback standing
 * in for a failed evaluation. That fail-closed guarantee lives in one place;
 * don't reintroduce a default here.
 */
export const useIsFeatureFlagEnabled = (flag: FeatureFlag): boolean => {
  const e2eFlagOverrides = useContext(E2eFlagOverridesContext);
  useFlagChangeSubscription();

  return (
    (e2eFlagOverrides
      ? e2eFlagOverrides.includes(flag)
      : flagClient.getBooleanValue(
          flag,
          featureFlagDefinitions[flag].defaultValue
        )) || getEnv(`REACT_APP_FEATURE_${flag}`, true) === 'true'
  );
};
